// Deleting the selected leads — one, several, or every lead on screen.
//
// Same frame as the job, retainer and client versions (DeleteConfirmModal, including
// the type-to-confirm guard), so only what is specific to a BULK LEAD delete is here:
//
//   - The dialog heads a COUNT, not a record, because that is what is being confirmed.
//     It is also what has to be typed to arm the button (`confirmWord`): on a single
//     record the name is the fact worth reading, on a selection the number is — a
//     mis-typed 3 for 30 is precisely the mistake this guard exists to stop. A
//     selection of one keeps the old behaviour and asks for that lead's name.
//   - The leads are LISTED BY NAME, so the selection is visible one last time and can
//     be checked against what was ticked. Long lists scroll rather than push the red
//     button off screen.
//   - What goes with them is one line: their tasks. Every other line of the client
//     dialog (retainers, jobs, invoices, payments, expenses, publications, files,
//     agreements) is structurally impossible on a lead, and the server has just proved
//     it for these exact rows — it refuses the whole request if any of them has work
//     beneath it. So the dialog says so in words instead of listing nine zeroes.
//
// The counts come from the server rather than from the board data already on screen:
// the board carries open tasks plus 90 days of completed ones, so counting there would
// under-report a lead with older history — a destruction dialog has to be exact.
import useAsync from '../../hooks/useAsync';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

export default function DeleteLeadsModal({ ids, onClose, onDeleted }) {
  // The id list is the identity of this preview: change the selection, re-read.
  const key = ids.join(',');
  const { data: preview, loading, error } = useAsync(
    () => clientsApi.leadsDeletionPreview(ids), [key]
  );

  const count = preview ? preview.count : 0;
  const single = count === 1 ? preview.leads[0] : null;
  // A lead can hold tasks and meetings — meeting a prospect is what the pipeline's
  // "פגישה" stage is about — and both cascade with the row. Zero-count lines are dropped,
  // so a bare lead still gets the short dialog it always had.
  const items = preview
    ? [
      { key: 'tasks', count: preview.tasks, label: he.common.deleteTasks },
      { key: 'meetings', count: preview.meetings, label: he.calendar.deleteMeetings },
      { key: 'notes', count: preview.notes, label: he.leads.deleteNotes },
      { key: 'contacts', count: preview.contacts, label: he.clients.deleteContacts },

    ].filter((line) => line.count > 0)
    : [];

  return (
    <DeleteConfirmModal
      title={he.leads.deleteTitle}
      loading={loading}
      error={error}
      name={preview && (single ? single.name : he.leads.deleteCount(count))}
      subtitle={single ? he.leadStages[single.stage] : null}
      confirmWord={single ? undefined : String(count)}
      items={items}
      onConfirm={() => clientsApi.removeLeads(ids)}
      onClose={onClose}
      onDeleted={onDeleted}
    >
      {preview && !single && (
        <div className="delete-named-list">
          <p>{he.leads.deleteListHeading}</p>
          <ul className="delete-lead-list">
            {preview.leads.map((l) => (
              <li key={l.id}>
                {l.name}
                <span className="muted"> · {he.leadStages[l.stage]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DeleteConfirmModal>
  );
}
